<script lang="ts">
  // MSE+WS-attached <video>. Renders for either a live stream or a temp
  // playback stream id; the parent decides which name to pass.
  import { onDestroy, onMount } from 'svelte';

  type Props = {
    /** Stream name to subscribe to via /ws/{name}. Switching this
        prop tears down the previous MS+WS and starts fresh. */
    streamName: string;
    /** Initial playbackRate (used by archive fast-forward). */
    rate?: number;
    /** When true the video is paused (WS keeps running so resume is
        instant). Used for off-screen and non-fullscreen-active cells
        to drop HEVC decode CPU/GPU. */
    paused?: boolean;
    /** Tells parent the <video> element so it can attach pause/play handlers. */
    onvideo?: (v: HTMLVideoElement) => void;
    /** Status updates ('live', '1234 kb/s', 'WS err', ...). */
    onstatus?: (text: string, bad?: boolean) => void;
  };

  let { streamName, rate = 1, paused = false, onvideo, onstatus }: Props = $props();

  // Safari на iPhone не даёт обычный MediaSource: с iOS 17.1 вместо него
  // ManagedMediaSource, и без этой ветки телефон показывает чёрный
  // прямоугольник. На десктопе и на iPad остаётся обычный MediaSource.
  const MSE: typeof MediaSource | undefined =
    (globalThis as unknown as { ManagedMediaSource?: typeof MediaSource })
      .ManagedMediaSource ?? globalThis.MediaSource;
  const IS_MANAGED = Boolean(
    (globalThis as unknown as { ManagedMediaSource?: unknown }).ManagedMediaSource,
  );

  const HEVC_MIME_CANDIDATES = [
    'video/mp4; codecs="hvc1.1.6.L150.B0"',
    'video/mp4; codecs="hvc1.1.6.L153.B0"',
    'video/mp4; codecs="hvc1.1.6.L156.B0"',
    'video/mp4; codecs="hvc1.1.6.L123.B0"',
    'video/mp4; codecs="hvc1.1.6.L120.B0"',
    'video/mp4; codecs="hvc1.1.6.L93.B0"',
    'video/mp4; codecs="hev1.1.6.L150.B0"',
  ];

  // Find an ASCII fourcc (e.g. "avc1") in a byte buffer. Init segments are
  // small structured MP4 boxes, so a plain byte-scan is safe and cheap.
  function findFourcc(u8: Uint8Array, fourcc: string): number {
    const a = fourcc.charCodeAt(0),
      b = fourcc.charCodeAt(1),
      c = fourcc.charCodeAt(2),
      d = fourcc.charCodeAt(3);
    for (let i = 0; i + 3 < u8.length; i++) {
      if (u8[i] === a && u8[i + 1] === b && u8[i + 2] === c && u8[i + 3] === d) return i;
    }
    return -1;
  }

  const hex2 = (n: number): string => n.toString(16).padStart(2, '0');

  // Decide the MSE MIME from the init segment's own boxes, so an AVC (h264)
  // camera and an HEVC (h265) camera each get the codec string they need.
  function mimeFromInit(buf: ArrayBuffer): string | null {
    const u8 = new Uint8Array(buf);
    if (findFourcc(u8, 'avc1') !== -1) {
      // Read exact profile/compat/level out of avcC for a precise codec string.
      const candidates: string[] = [];
      const c = findFourcc(u8, 'avcC');
      if (c !== -1 && c + 7 < u8.length) {
        // avcC payload: [0]=configVersion, [1]=profile, [2]=compat, [3]=level
        candidates.push(
          `video/mp4; codecs="avc1.${hex2(u8[c + 5])}${hex2(u8[c + 6])}${hex2(u8[c + 7])}"`,
        );
      }
      // Generic fallbacks (high/main/baseline) if the exact string is refused.
      candidates.push(
        'video/mp4; codecs="avc1.640029"',
        'video/mp4; codecs="avc1.4d401f"',
        'video/mp4; codecs="avc1.42e01e"',
      );
      for (const m of candidates) if (MSE!.isTypeSupported(m)) return m;
      return null;
    }
    // Точную строку кодека берём из самого hvcC, а не угадываем уровень:
    // Safari (особенно на iPhone) отклоняет несовпадающий уровень целиком.
    const candidates: string[] = [];
    const h = findFourcc(u8, 'hvcC');
    if (h !== -1 && h + 16 < u8.length) {
      const p0 = u8[h + 5]; // profile_space(2) | tier(1) | profile_idc(5)
      const profileSpace = p0 >> 6;
      const tier = (p0 >> 5) & 1 ? 'H' : 'L';
      const profileIdc = p0 & 0x1f;
      const level = u8[h + 16]; // general_level_idc
      const space = profileSpace ? String.fromCharCode(64 + profileSpace) : '';
      for (const fourcc of ['hvc1', 'hev1']) {
        candidates.push(
          `video/mp4; codecs="${fourcc}.${space}${profileIdc}.6.${tier}${level}.B0"`,
        );
      }
    }
    candidates.push(...HEVC_MIME_CANDIDATES);
    for (const m of candidates) if (MSE!.isTypeSupported(m)) return m;
    return null;
  }

  let videoEl: HTMLVideoElement | undefined = $state();
  let ws: WebSocket | null = null;
  let ms: MediaSource | null = null; // MediaSource либо ManagedMediaSource
  let sb: SourceBuffer | null = null;
  /** Строка кодека, которую мы запросили у браузера — нужна в сообщениях. */
  let chosen = '?';
  /** Уже показали настоящую причину отказа — не затирать её общей «WS close». */
  let reported = false;
  /** Разбираем ячейку намеренно — переподключаться не нужно. */
  let closing = false;
  let retry = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setInterval> | null = null;
  let lastData = 0;
  /** Столько тишины считаем обрывом: поток идёт непрерывно, пауз тут не бывает. */
  const STALL_MS = 20_000;

  function setStat(text: string, bad = false): void {
    onstatus?.(text, bad);
  }

  /** Сообщить причину отказа: она важнее последующего «соединение закрыто». */
  function fail(text: string): void {
    reported = true;
    onstatus?.(text, true);
  }

  function teardown(): void {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (watchdog) {
      clearInterval(watchdog);
      watchdog = null;
    }
    try {
      ws?.close();
    } catch (_) {
      /* ignore */
    }
    ws = null;
    if (videoEl) {
      try {
        videoEl.pause();
      } catch (_) {
        /* ignore */
      }
      try {
        (videoEl as HTMLVideoElement & { srcObject: unknown }).srcObject = null;
      } catch (_) {
        /* браузер без srcObject на video */
      }
      videoEl.removeAttribute('src');
      videoEl.load();
    }
    ms = null;
    sb = null;
  }

  /// Обрыв — штатная ситуация: мобильная сеть моргает, вкладка уходит в фон,
  /// прокси подрезает долгие соединения. Молча оставлять застывший кадр
  /// нельзя: человек видит картинку и думает, что смотрит происходящее.
  function scheduleReconnect(why: string): void {
    if (closing || reconnectTimer) return;
    retry = Math.min(retry + 1, 6);
    const delay = Math.min(1000 * 2 ** (retry - 1), 15_000);
    setStat(`${why}, переподключение…`, true);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (closing) return;
      teardown();
      attach();
    }, delay);
  }

  function attach(): void {
    if (!videoEl) return;
    reported = false;
    lastData = Date.now();
    // Сторож на случай, когда соединение формально живо, а данные не идут:
    // такой обрыв сам себя не объявляет.
    watchdog = setInterval(() => {
      if (!closing && Date.now() - lastData > STALL_MS) {
        scheduleReconnect('поток замер');
      }
    }, 5000);
    if (!MSE) {
      // Лучше честная надпись, чем чёрный прямоугольник без объяснений.
      fail('браузер не умеет MSE');
      return;
    }
    ms = new MSE();
    videoEl.muted = true;
    videoEl.autoplay = true;
    videoEl.playsInline = true;
    if (IS_MANAGED) {
      // ManagedMediaSource обязателен к подключению через srcObject и
      // требует отключить AirPlay, иначе поток не стартует.
      videoEl.disableRemotePlayback = true;
      try {
        (videoEl as HTMLVideoElement & { srcObject: unknown }).srcObject = ms;
      } catch (_) {
        videoEl.src = URL.createObjectURL(ms);
      }
    } else {
      videoEl.src = URL.createObjectURL(ms);
    }

    // Декодер может отказать уже после запуска — без этого ячейка просто
    // чернеет, и причина остаётся неизвестной.
    videoEl.addEventListener('error', () => {
      const err = videoEl?.error;
      if (err) fail(`видео: код ${err.code} (${chosen})`);
    });

    const localMs = ms;
    localMs.addEventListener('sourceopen', () => {
      const queue: ArrayBuffer[] = [];
      let appending = false;
      let buf: SourceBuffer | null = null;

      const tryAppend = (): void => {
        if (!buf || appending || queue.length === 0 || buf.updating || localMs.readyState !== 'open')
          return;
        appending = true;
        const next = queue.shift();
        if (!next) {
          appending = false;
          return;
        }
        try {
          buf.appendBuffer(next);
        } catch (e) {
          appending = false;
          const why = e instanceof Error ? e.name : 'ошибка';
          fail(`отвергнут кадр: ${why} (${chosen})`);
        }
      };

      // The first WS message is the fMP4 init segment (server sends it before
      // any fragment). We read the codec from its own boxes and create the
      // SourceBuffer to match — so h264 and h265 cameras both play.
      const onInit = (initBuf: ArrayBuffer): boolean => {
        const mime = mimeFromInit(initBuf);
        if (!mime) {
          fail('кодек не принят браузером');
          return false;
        }
        // Короткая пометка кодека: когда ячейка молчит, надо видеть, ЧТО
        // именно мы попросили у браузера, иначе причину не найти.
        chosen = mime.replace('video/mp4; codecs=', '').replace(/"/g, '');
        try {
          buf = localMs.addSourceBuffer(mime);
          buf.mode = 'segments';
        } catch (e) {
          fail(`не создать буфер (${chosen})`);
          return false;
        }
        const b = buf;
        b.addEventListener('error', () => fail(`ошибка буфера (${chosen})`));
        b.addEventListener('updateend', () => {
          appending = false;
          try {
            if (b.buffered.length > 0 && videoEl) {
              const start = b.buffered.start(0);
              const end = b.buffered.end(0);
              if (videoEl.currentTime < start && videoEl.readyState < 2) {
                try {
                  videoEl.currentTime = start;
                } catch (_) {
                  /* ignore */
                }
                videoEl.play().catch(() => {});
              }
              if (end - start > 30 && !b.updating) {
                b.remove(start, end - 30);
                return;
              }
            }
          } catch (_) {
            /* ignore */
          }
          tryAppend();
        });
        sb = b;
        return true;
      };

      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${proto}//${location.host}/ws/${encodeURIComponent(streamName)}`);
      ws.binaryType = 'arraybuffer';
      let bytes = 0;
      let last = performance.now();
      let gotInit = false;
      ws.onopen = () => {
        retry = 0;
        lastData = Date.now();
        setStat('live');
      };
      ws.onerror = () => {
        if (!reported) setStat('сбой связи', true);
      };
      ws.onclose = () => {
        // Причину отказа кодека не затираем — переподключение её не исправит.
        if (reported) return;
        scheduleReconnect('связь прервана');
      };
      ws.onmessage = (ev): void => {
        if (!(ev.data instanceof ArrayBuffer)) return;
        lastData = Date.now();
        bytes += ev.data.byteLength;
        if (!gotInit) {
          gotInit = true;
          if (!onInit(ev.data)) {
            try {
              ws?.close();
            } catch (_) {
              /* ignore */
            }
            return;
          }
        }
        queue.push(ev.data);
        tryAppend();
        const now = performance.now();
        if (now - last > 1500) {
          const kbps = Math.round((bytes * 8) / (now - last));
          setStat(`${kbps} kb/s`);
          bytes = 0;
          last = now;
        }
      };
    });
  }

  onMount(() => {
    if (videoEl && onvideo) onvideo(videoEl);
    attach();
  });

  onDestroy(() => {
    closing = true;
    teardown();
  });

  // Re-attach on streamName change.
  let prevName = $state(streamName);
  $effect(() => {
    if (streamName !== prevName) {
      prevName = streamName;
      closing = true;
      teardown();
      closing = false;
      retry = 0;
      attach();
    }
  });

  // Apply rate on change (for client-side fast-forward).
  $effect(() => {
    if (videoEl && rate > 0) {
      try {
        videoEl.playbackRate = rate;
      } catch (_) {
        /* ignore */
      }
    }
  });

  // Pause / resume on prop change AND on IntersectionObserver visibility.
  let visible = $state(true);
  $effect(() => {
    if (!videoEl) return;
    const wantPaused = paused || !visible;
    if (wantPaused && !videoEl.paused) {
      videoEl.pause();
    } else if (!wantPaused && videoEl.paused) {
      videoEl.play().catch(() => {});
    }
  });

  // IntersectionObserver — pause when scrolled out of viewport.
  $effect(() => {
    if (!videoEl) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e) visible = e.isIntersecting;
      },
      { threshold: 0.05 },
    );
    io.observe(videoEl);
    return () => io.disconnect();
  });

</script>

<video bind:this={videoEl}></video>

<style>
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    background: #000;
  }
</style>
